import { CheckCircle2, Circle, Loader2, MessageSquareText, Play, XCircle } from "lucide-react";
import * as React from "react";
import { v4 as uuidv4 } from "uuid";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

import { useBuilderStore } from "@/lib/builder/builder-store";
import {
  NL_STEPS,
  parseBatchInstructions,
  type NlCommandType,
  type ParsedInstruction,
} from "@/lib/builder/nl-commands";
import { cn } from "@/lib/utils";
import { generateAppSdd, getDebugSchema } from "@/services/debugpavilion";

interface InstructionEntry {
  id: string;
  raw: string;
  type: NlCommandType;
  status: "pending" | "running" | "done" | "error";
  /** 当前流式步骤索引（NL_STEPS） */
  stepIndex: number;
  error?: string;
}

const TYPE_LABELS: Record<NlCommandType, string> = {
  add: "新增",
  modify: "修改",
  remove: "删除",
  rename: "重命名",
  other: "指令",
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

interface NlCanvasProps {
  /** 当前已保存的应用 id（未保存草稿为 null） */
  savedAppId?: string | null;
  /** generate 新建应用时回调，builder 用它记录 savedAppId */
  onAppCreated?: (appId: string) => void;
}

/**
 * 画布② 自然语言画布（ARCHITECTURE_DESIGN.md §5.2）：
 * 输入修改指令 → 调 SDD/generate 更新 AppSchema → 热更新画布①（builder-store 订阅已具备）。
 * 支持多行批量指令逐条执行；每条指令以"分析 → 修改 schema → 完成"步骤流展示进度。
 */
export function NlCanvas({ savedAppId, onAppCreated }: NlCanvasProps) {
  const setSchema = useBuilderStore((state) => state.setSchema);
  const [input, setInput] = React.useState("");
  const [entries, setEntries] = React.useState<InstructionEntry[]>([]);
  const [running, setRunning] = React.useState(false);

  const updateEntry = (id: string, patch: Partial<InstructionEntry>) =>
    setEntries((prev) => prev.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)));

  const runInstruction = async (parsed: ParsedInstruction) => {
    const id = uuidv4();
    const entry: InstructionEntry = {
      id,
      raw: parsed.raw,
      type: parsed.type,
      status: "running",
      stepIndex: 0,
    };
    setEntries((prev) => [entry, ...prev]);

    const advance = async () => {
      await sleep(400);
      updateEntry(id, { stepIndex: 1 });
      return id;
    };

    try {
      await advance();
      const generation = await generateAppSdd({
        requirement: parsed.raw,
        appId: savedAppId ?? undefined,
        sync: false,
      });
      const appId = generation.app_id;
      if (!savedAppId && onAppCreated) onAppCreated(appId);
      updateEntry(id, { stepIndex: 2 });
      const detail = await getDebugSchema(appId);
      // 热更新：builder-store 订阅后画布① 自动反映
      setSchema(detail.app_schema);
      await sleep(300);
      updateEntry(id, { status: "done", stepIndex: NL_STEPS.length - 1 });
    } catch (error) {
      updateEntry(id, { status: "error", error: String(error) });
    }
  };

  const handleExecute = async () => {
    if (!input.trim() || running) return;
    const instructions = parseBatchInstructions(input);
    if (instructions.length === 0) return;
    setInput("");
    setRunning(true);
    try {
      for (const parsed of instructions) {
        await runInstruction(parsed);
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="flex h-full flex-col" data-testid="nl-canvas">
      <div className="flex items-center gap-2 border-b bg-muted/40 px-3 py-2">
        <MessageSquareText className="h-4 w-4 text-primary" />
        <span className="text-xs font-semibold">画布② 自然语言</span>
        <span className="text-[10px] text-muted-foreground">输入描述 → AI 修改 Schema → 热更新画布①</span>
      </div>

      <div className="flex-1 space-y-2 overflow-auto p-3">
        {entries.length === 0 && (
          <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
            示例：<br />
            <span className="text-foreground/70">增加保存配色方案功能</span>
            <br />
            <span className="text-foreground/70">把列表页的按钮改成圆角</span>
            <br />
            <span className="text-foreground/70">删除首页的搜索栏</span>
            <br />
            <br />
            支持多行批量指令，逐条执行。
          </div>
        )}

        {entries.map((entry) => (
            <div
              key={entry.id}
              data-testid={`nl-entry-${entry.status}`}
              className={cn(
                "rounded-md border p-2.5",
                entry.status === "error" && "border-destructive/40 bg-destructive/5",
                entry.status === "done" && "border-green-500/30 bg-green-500/5",
              )}
            >
              <div className="flex items-center gap-2">
                <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                  {TYPE_LABELS[entry.type]}
                </span>
                <span className="flex-1 text-xs">{entry.raw}</span>
                {entry.status === "done" && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                {entry.status === "error" && <XCircle className="h-4 w-4 text-destructive" />}
              </div>

              <div className="mt-2 flex items-center gap-2">
                {NL_STEPS.map((step, idx) => (
                  <React.Fragment key={step.id}>
                    {idx > 0 && <div className="h-px w-3 bg-border" />}
                    <div
                      className={cn(
                        "flex items-center gap-1 text-[10px]",
                        idx < entry.stepIndex && "text-green-600",
                        idx === entry.stepIndex && entry.status === "running" && "font-semibold text-primary",
                        idx > entry.stepIndex && "text-muted-foreground/50",
                      )}
                    >
                      {idx < entry.stepIndex ? (
                        <CheckCircle2 className="h-3 w-3" />
                      ) : idx === entry.stepIndex && entry.status === "running" ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Circle className="h-3 w-3" />
                      )}
                      {step.label}
                    </div>
                  </React.Fragment>
                ))}
              </div>

              {entry.status === "error" && (
                <div className="mt-1.5 text-[10px] text-destructive">{entry.error}</div>
              )}
            </div>
          ))}
      </div>

      <div className="border-t bg-card p-3">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={"输入修改指令，例如：\n增加保存配色方案功能\n把标题改成蓝色\n（支持多行批量）"}
          className="min-h-[72px] resize-none text-sm"
          data-testid="nl-input"
        />
        <Button
          className="mt-2 w-full"
          size="sm"
          disabled={running || !input.trim()}
          onClick={() => void handleExecute()}
          data-testid="nl-execute"
        >
          {running ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Play className="mr-1.5 h-4 w-4" />}
          {running ? "执行中..." : "执行指令"}
        </Button>
      </div>
    </div>
  );
}
